import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Send, Loader2, MapPin, Calendar, Train, Plane, Bus, Check, ArrowRight, MessageCircle } from 'lucide-react';

import Mascot from './Mascot';
import {
    useLeadBotState,
    recordNudge,
    snoozeLeadBot,
    isLeadBotSnoozed,
    markLeadSubmitted,
    markBookingLeadSubmitted,
    markReasonGiven,
    setBotAvailable,
    resolveLeaveRequest,
    getLeadBotState,
} from './leadBotStore';
import {
    BOT_NAME,
    LIMITS,
    SEARCH_TRIGGERS,
    HOME_TRIGGERS,
    PAIN_TRIGGERS,
    BOOKING_TRIGGERS,
    BOOKING_PAIN_TRIGGERS,
    BOOKING_TRIGGER_REASON,
    BOOKING_NUDGES,
    BOOKING_PRIMARY,
    BOOKING_REASONS,
    BOOKING_MENU_REASONS,
    REASON_LABEL,
    NUDGES,
    NUDGE_PRIMARY,
    MOOD_FOR_TRIGGER,
    COPY,
    FLEX_OPTIONS,
    MODE_OPTIONS,
    BOT_TRIGGER_TYPE,
    MASCOT_THEME,
    FAB_COPY,
    shortPlace,
} from './leadBotScript';
import { searchLocations } from './locationSearch';
import { useContactLead } from '../../hooks/useContactLead';
import { FEATURES } from '../../config/features';
import { COUNTRY_CODES, DEFAULT_COUNTRY_CODE } from '../../config/contactConfig';
import { getCallStatus, describeNextOpen } from '../../utils/callHours';
import './LeadBot.css';

// ───────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────
const wait = (ms) => new Promise(r => setTimeout(r, ms));
let msgSeq = 0;
const msgId = () => `m${++msgSeq}`;

const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const todayISO = () => toISO(new Date());
const addDays = (iso, n) => {
    const [y, m, d] = iso.split('-').map(Number);
    return toISO(new Date(y, m - 1, d + n));
};
const prettyShort = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};
const prettyLong = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const phoneRule = (code) => COUNTRY_CODES.find(c => c.code === code) || { minLen: 6, maxLen: 14 };
const validatePhone = (code, digits) => {
    if (!digits) return 'Enter your mobile number';
    const { minLen, maxLen } = phoneRule(code);
    if (digits.length < minLen || digits.length > maxLen) {
        return minLen === maxLen ? `Enter a ${minLen}-digit mobile number` : `Enter a ${minLen} to ${maxLen} digit mobile number`;
    }
    if (code === '+91' && !/^[6-9]/.test(digits)) return 'Indian mobile numbers start with 6, 7, 8 or 9';
    return '';
};

const TRAIN_CLASSES = ['1A', '2A', '3A', 'SL', 'CC', '2S'];
const FLIGHT_CLASSES = ['Economy', 'Premium', 'Business', 'First'];

const tripClassLabel = (s) => {
    const classes = s?.seat_classes || [];
    const modes = s?.modes || [];
    if (modes.length === 1 && modes[0] === 'Train') return classes.find(c => TRAIN_CLASSES.includes(c)) || '';
    if (modes.length === 1 && modes[0] === 'Flight') return classes.find(c => FLIGHT_CLASSES.includes(c)) || '';
    return '';
};

const normalizeModes = (modes) => {
    const list = Array.isArray(modes) ? modes : [];
    if (list.includes('All')) return [...MODE_OPTIONS];
    return MODE_OPTIONS.filter(m => list.includes(m));
};

const MODE_ICONS = { Train, Flight: Plane, Bus };

const tripSummary = (s) => {
    if (!s) return 'this trip';
    const cls = tripClassLabel(s);
    return `${shortPlace(s.src_name)} → ${shortPlace(s.dest_name)}, ${prettyShort(s.travel_date_from)}${cls ? `, ${cls}` : ''}`;
};

const joinList = (arr) => (arr.length <= 1 ? arr.join('') : `${arr.slice(0, -1).join(', ')} or ${arr[arr.length - 1]}`);

const FLEX_TEXT = {
    exact: 'Fixed date.',
    pm1: 'Dates are flexible by 1 day either side.',
    pm3: 'Dates are flexible by 2 to 3 days either side.',
};

// Lead message written from what the user picked in the chat
const buildMessage = ({ intent, src, dest, from, to, baseDate, modes, classes, flex }) => {
    if (!src || !dest) return 'Please call me back. I need help planning a trip. (via RouteChef assistant)';
    const route = `from ${shortPlace(src)} to ${shortPlace(dest)}`;
    const when = from !== to
        ? `around ${prettyLong(baseDate)} (any day between ${prettyLong(from)} and ${prettyLong(to)})`
        : `on ${prettyLong(from)}`;
    const how = modes.length ? ` by ${joinList(modes.map(m => m.toLowerCase()))}` : '';
    const cls = classes.length ? `, preferred class ${classes.join(' / ')}` : '';
    const flexLine = FLEX_TEXT[flex] || '';
    const ask = intent === 'seat_alert'
        ? `Seat alert: please WhatsApp me when a confirmed seat opens ${route} ${when}${how}${cls}.`
        : `Please help me get confirmed tickets ${route} ${when}${how}${cls}.`;
    return `${ask} ${flexLine} (via RouteChef assistant)`.replace(/\s+/g, ' ').trim();
};

// ── Booking page helpers ──────────────────────────────────────────────────
const reasonByValue = (v) => BOOKING_REASONS.find(r => r.value === v);
const reasonLabel = (v) => reasonByValue(v)?.label || REASON_LABEL[v] || v || '';
const bookingModeLabel = (b) => (b?.mode === 'bus' ? 'Bus' : 'Flight');

// What the CLICKED row gets on create (same shape as the search page's contactSearchData)
const bookingSearchData = (b) => (b ? {
    src_name: b.src_name || '',
    src_code: b.src_code || '',
    dest_name: b.dest_name || '',
    dest_code: b.dest_code || '',
    travel_date_from: b.travel_date || '',
    travel_date_to: b.travel_date || '',
    modes: [bookingModeLabel(b)],
    seat_classes: [],
} : {});

// '+919876543210' / '09876543210' / '9876543210' → '9876543210', else ''
const prefillDigits = (raw) => {
    let d = String(raw || '').replace(/\D/g, '');
    if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
    if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
    return /^[6-9]\d{9}$/.test(d) ? d : '';
};

// Plain-English note for ops. Carries the booking ref so the team can open the booking row.
const buildBookingMessage = ({ b, reason, reasonText, dropOnly }) => {
    const route = b?.src_name && b?.dest_name ? ` ${shortPlace(b.src_name)} to ${shortPlace(b.dest_name)}` : '';
    const when = b?.travel_date ? ` on ${prettyLong(b.travel_date)}` : '';
    const parts = [`${dropOnly ? 'Left the' : 'Needs help with the'} ${bookingModeLabel(b).toLowerCase()} booking${route}${when}.`];
    if (reason) parts.push(`Issue: ${reasonLabel(reason)}${reasonText ? ` (${reasonText})` : ''}.`);
    if (b?.amount) parts.push(`Amount Rs ${Number(b.amount).toLocaleString('en-IN')}.`);
    if (b?.stage) parts.push(`Stage: ${b.stage}.`);
    if (b?.rcBookingId) parts.push(`Booking ref ${b.rcBookingId}.`);
    parts.push('(via RouteChef assistant)');
    return parts.join(' ');
};

// ── "User is filling a form" detection ────────────────────────────────────
// The bot steps aside while the user types in a page input, while an autocomplete
// list or date picker is open, or while the mobile keyboard is up. Works without
// touching the forms: common picker / listbox selectors are detected, and any
// element can opt in with a data-leadbot-hide attribute.
const FORM_POPUP_SELECTORS = [
    '[data-leadbot-hide]',
    '[role="listbox"]',
    '[role="dialog"][aria-modal="true"]',
    '.react-datepicker-popper',
    '.react-datepicker__portal',
    '.react-calendar',
    '.rdp',
    '.rdrCalendarWrapper',
    '.flatpickr-calendar.open',
    '.MuiPickersPopper-root',
    '.MuiAutocomplete-popper',
    '.MuiPopover-root',
];
const TEXT_INPUT_SELECTOR = 'input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]):not([type="range"]), textarea, select, [contenteditable="true"]';
const inBot = (el) => !!(el && el.closest && el.closest('[data-leadbot-root]'));
const isMobileView = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(max-width: 639px)').matches;

const useFormBusy = (active) => {
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (!active || typeof document === 'undefined') { setBusy(false); return undefined; }
        let checkTimer = null;
        let offTimer = null;

        const compute = () => {
            const a = document.activeElement;
            const typing = !!(a && a !== document.body && !inBot(a) && a.matches && a.matches(TEXT_INPUT_SELECTOR));
            let popup = false;
            for (const sel of FORM_POPUP_SELECTORS) {
                const els = document.querySelectorAll(sel);
                for (let i = 0; i < els.length; i++) {
                    if (!inBot(els[i]) && els[i].getClientRects().length > 0) { popup = true; break; }
                }
                if (popup) break;
            }
            const vv = window.visualViewport;
            const keyboard = vv ? (window.innerHeight - vv.height) > 150 : false;
            return typing || popup || keyboard;
        };

        const run = () => {
            clearTimeout(checkTimer);
            checkTimer = setTimeout(() => {
                if (compute()) {
                    clearTimeout(offTimer);
                    setBusy(true);
                } else {
                    // Small delay so moving From → To → date doesn't make the bot flicker back in
                    clearTimeout(offTimer);
                    offTimer = setTimeout(() => setBusy(false), 700);
                }
            }, 60);
        };

        const events = ['focusin', 'focusout', 'click', 'touchend', 'keyup'];
        events.forEach(e => document.addEventListener(e, run, true));
        const vv = window.visualViewport;
        if (vv) vv.addEventListener('resize', run);
        const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(run) : null;
        if (mo) mo.observe(document.body, { childList: true, subtree: true });
        run();

        return () => {
            clearTimeout(checkTimer);
            clearTimeout(offTimer);
            events.forEach(e => document.removeEventListener(e, run, true));
            if (vv) vv.removeEventListener('resize', run);
            if (mo) mo.disconnect();
        };
    }, [active]);
    return busy;
};

// Keeps the sheet above the mobile keyboard
const useKeyboardInset = (active) => {
    const [inset, setInset] = useState(0);
    useEffect(() => {
        const vv = typeof window !== 'undefined' ? window.visualViewport : null;
        if (!active || !vv) { setInset(0); return undefined; }
        const update = () => setInset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
        update();
        vv.addEventListener('resize', update);
        vv.addEventListener('scroll', update);
        return () => {
            vv.removeEventListener('resize', update);
            vv.removeEventListener('scroll', update);
        };
    }, [active]);
    return inset;
};

// ───────────────────────────────────────────────────────────────────────────
// Small UI pieces
// ───────────────────────────────────────────────────────────────────────────
const Chips = ({ options, onPick }) => (
    <div className="flex flex-wrap gap-2">
        {options.map(o => (
            <button
                key={o.value}
                type="button"
                onClick={() => onPick(o)}
                className={`min-h-[40px] px-3.5 rounded-full border text-sm font-medium transition-colors
                    ${o.primary
                        ? 'bg-orange-500 border-orange-500 text-white hover:bg-orange-600 active:bg-orange-700'
                        : 'bg-white border-orange-200 text-orange-700 hover:bg-orange-50 active:bg-orange-100'}
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400`}
            >
                {o.label}
            </button>
        ))}
    </div>
);

const TypingDots = () => (
    <div className="flex items-end gap-2">
        <div className="rounded-2xl rounded-bl-md bg-slate-100 px-3.5 py-3 flex gap-1" aria-label={`${BOT_NAME} is typing`}>
            <span className="lb-typing-dot" /><span className="lb-typing-dot lb-d2" /><span className="lb-typing-dot lb-d3" />
        </div>
    </div>
);

const LocationInput = ({ label, value, onChange, autoFocus }) => {
    const [text, setText] = useState(value?.label || '');
    const [focused, setFocused] = useState(false);
    const results = useMemo(() => (focused && !value ? searchLocations(text) : []), [focused, text, value]);

    return (
        <div>
            <div className="relative">
                <MapPin size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
                <input
                    type="text"
                    autoComplete="off"
                    autoFocus={autoFocus}
                    aria-label={label}
                    placeholder={label}
                    value={text}
                    onFocus={(e) => { setFocused(true); if (value) e.target.select(); }}
                    onBlur={() => setTimeout(() => setFocused(false), 150)}
                    onChange={(e) => { setText(e.target.value); if (value) onChange(null); }}
                    className="w-full h-11 rounded-xl border border-slate-200 bg-white pl-9 pr-3 text-base sm:text-sm text-slate-900
                               placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                />
            </div>
            {results.length > 0 && (
                <ul role="listbox" className="mt-1 max-h-40 overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-sm">
                    {results.map(item => (
                        <li
                            key={`${item.type}-${item.code}`}
                            role="option"
                            aria-selected="false"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => { setText(item.label); onChange(item); setFocused(false); }}
                            className="flex items-center gap-2 px-3 py-2.5 text-sm cursor-pointer hover:bg-orange-50"
                        >
                            {item.type === 'airport'
                                ? <Plane size={14} className="shrink-0 text-slate-400" aria-hidden="true" />
                                : <Train size={14} className="shrink-0 text-slate-400" aria-hidden="true" />}
                            <span className="min-w-0 flex-1 truncate text-slate-800">{item.name}</span>
                            <span className="shrink-0 text-xs font-semibold text-slate-500">{item.code}</span>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const RouteStep = ({ onSubmit }) => {
    const [src, setSrc] = useState(null);
    const [dest, setDest] = useState(null);
    const [date, setDate] = useState(addDays(todayISO(), 1));
    const ready = src && dest && date && src.code !== dest.code;

    return (
        <div className="space-y-2">
            <LocationInput label="From: station, airport or city" value={src} onChange={setSrc} autoFocus />
            <LocationInput label="To: station, airport or city" value={dest} onChange={setDest} />
            <div className="flex gap-2">
                <div className="relative flex-1">
                    <Calendar size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" aria-hidden="true" />
                    <input
                        type="date"
                        aria-label="Travel date"
                        value={date}
                        min={todayISO()}
                        onChange={(e) => setDate(e.target.value)}
                        className="w-full h-11 rounded-xl border border-slate-200 bg-white pl-9 pr-2 text-base sm:text-sm text-slate-900
                                   focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
                    />
                </div>
                <button
                    type="button"
                    disabled={!ready}
                    onClick={() => onSubmit({ src, dest, date })}
                    className="shrink-0 inline-flex items-center gap-1.5 h-11 px-4 rounded-xl bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold
                               disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Next <ArrowRight size={15} aria-hidden="true" />
                </button>
            </div>
        </div>
    );
};

const ModePicker = ({ initial, onSubmit }) => {
    const [picked, setPicked] = useState(initial.length ? initial : ['Train']);
    const toggle = (m) => setPicked(prev => (prev.includes(m)
        ? prev.filter(x => x !== m)
        : MODE_OPTIONS.filter(x => x === m || prev.includes(x))));

    return (
        <div className="space-y-2.5">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Modes of travel">
                {MODE_OPTIONS.map(m => {
                    const Icon = MODE_ICONS[m];
                    const on = picked.includes(m);
                    return (
                        <button
                            key={m}
                            type="button"
                            aria-pressed={on}
                            onClick={() => toggle(m)}
                            className={`inline-flex items-center gap-1.5 min-h-[40px] px-3.5 rounded-full border text-sm font-medium transition-colors
                                ${on
                                    ? 'bg-orange-500 border-orange-500 text-white'
                                    : 'bg-white border-slate-200 text-slate-600 hover:border-orange-300'}`}
                        >
                            {on ? <Check size={14} aria-hidden="true" /> : <Icon size={14} aria-hidden="true" />}
                            {m}
                        </button>
                    );
                })}
            </div>
            <button
                type="button"
                disabled={!picked.length}
                onClick={() => onSubmit(picked)}
                className="w-full inline-flex items-center justify-center gap-1.5 h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold
                           disabled:opacity-50 disabled:cursor-not-allowed"
            >
                Continue <ArrowRight size={15} aria-hidden="true" />
            </button>
        </div>
    );
};

const PhoneStep = ({
    intent, countryCode, setCountryCode, phone, setPhone, error, busy, onSubmit,
}) => (
    <form
        className="space-y-2"
        onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
        noValidate
    >
        <div className="flex gap-2">
            <select
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
                aria-label="Country code"
                className="h-11 w-[92px] shrink-0 rounded-xl border border-slate-200 bg-white pl-2 pr-1 text-base sm:text-sm text-slate-900
                           focus:outline-none focus:ring-2 focus:ring-orange-400"
            >
                {COUNTRY_CODES.map(c => <option key={c.iso} value={c.code}>{c.flag} {c.code}</option>)}
            </select>
            <input
                type="tel"
                inputMode="numeric"
                autoComplete="tel-national"
                autoFocus
                aria-label="Mobile number"
                aria-invalid={!!error}
                placeholder={countryCode === '+91' ? '98765 43210' : 'Mobile number'}
                value={phone}
                maxLength={18}
                onChange={(e) => setPhone(e.target.value.replace(/[^\d\s]/g, ''))}
                className={`h-11 min-w-0 flex-1 rounded-xl border bg-white px-3 text-base sm:text-sm text-slate-900 placeholder:text-slate-400
                            focus:outline-none focus:ring-2 focus:border-transparent
                            ${error ? 'border-red-400 focus:ring-red-400' : 'border-slate-200 focus:ring-orange-400'}`}
            />
            <button
                type="submit"
                disabled={busy}
                aria-label="Send"
                className="shrink-0 w-11 h-11 rounded-xl bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white flex items-center justify-center
                           disabled:opacity-60"
            >
                {busy ? <Loader2 size={18} className="animate-spin" aria-hidden="true" /> : <Send size={17} aria-hidden="true" />}
            </button>
        </div>
        {error && <p className="text-xs text-red-600" role="alert">{error}</p>}
        <p className="flex items-center gap-1.5 text-xs text-slate-500">
            <MessageCircle size={13} className="text-green-600" aria-hidden="true" />
            {intent === 'seat_alert' ? 'Alerts come on WhatsApp. No spam, promise.' : COPY.phoneNote}
        </p>
    </form>
);

const OtherTextStep = ({ onSubmit }) => {
    const [text, setText] = useState('');
    const ok = text.trim().length >= 2;
    return (
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (ok) onSubmit(text.trim()); }}>
            <input
                type="text"
                autoFocus
                maxLength={200}
                aria-label="What went wrong"
                placeholder="e.g. coupon didn't apply"
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="h-11 min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 text-base sm:text-sm text-slate-900 placeholder:text-slate-400
                           focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
            />
            <button
                type="submit"
                disabled={!ok}
                aria-label="Send"
                className="shrink-0 w-11 h-11 rounded-xl bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center disabled:opacity-50"
            >
                <Send size={17} aria-hidden="true" />
            </button>
        </form>
    );
};

// ───────────────────────────────────────────────────────────────────────────
// LeadBot
// ───────────────────────────────────────────────────────────────────────────
/**
 * Mount ONCE inside the Router (e.g. in App.jsx, outside <Routes>).
 * Shows on the home page ("/"), the route search page (signals via useLeadBotSignals)
 * and the booking pages (signals via useBookingLeadSignals). Hidden everywhere else, including /chat.
 * On booking pages it also runs the exit survey when the page calls requestLeave / leaveBookingPage.
 * Leads go through the same submitContactAction as the ContactModal.
 */
const LeadBot = () => {
    const enabled = !!FEATURES.ENABLE_LEAD_BOT;
    const bot = useLeadBotState();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    // Same hook + backend contract as the ContactModal: open (creates the CLICKED row), then submit
    const { loadConfig, callUs, openContactModal, submitContactAction } = useContactLead();

    const page = bot.page === 'search' || bot.page === 'booking'
        ? bot.page
        : (pathname === '/' ? 'home' : null);
    const isBooking = page === 'booking';
    const suppressed = Object.values(bot.suppressed || {}).some(Boolean);
    // User is typing / picking in a page form: no nudges, no label, and on mobile no dock at all
    const formBusy = useFormBusy(enabled && !!page);

    const [open, setOpen] = useState(false);
    const [bubble, setBubble] = useState(null);       // { trigger, text }
    const [mood, setMood] = useState('idle');
    const [messages, setMessages] = useState([]);
    const [step, setStep] = useState(null);
    const [typing, setTyping] = useState(false);
    const [unread, setUnread] = useState(false);
    const [ready, setReady] = useState(false);         // dock enters after the page has painted
    const [extended, setExtended] = useState(true);    // extended FAB (avatar + label) vs avatar only
    const [greeted, setGreeted] = useState(false);     // first-appearance wave + pulse done
    const [local, setLocal] = useState({ exitIntent: false, homeIdle: false, homeScroll: false });

    const [countryCode, setCountryCode] = useState(DEFAULT_COUNTRY_CODE);
    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [busy, setBusy] = useState(false);

    const convRef = useRef(0);
    const leadRef = useRef({});
    const listRef = useRef(null);
    const kbInset = useKeyboardInset(open);

    // Contact config (call hours) for this bot's own hook instance
    useEffect(() => { if (enabled && loadConfig) loadConfig(); }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps

    // Booking pages only route their leave/back through the bot while it can actually answer
    useEffect(() => {
        setBotAvailable(enabled && isBooking);
        return () => setBotAvailable(false);
    }, [enabled, isBooking]);

    // A booking page asked to leave (close button / back press): open the exit survey
    const leaveSeenRef = useRef(null);
    useEffect(() => {
        const req = bot.leaveRequest;
        if (!enabled || !isBooking || !req || leaveSeenRef.current === req.id) return;
        leaveSeenRef.current = req.id;
        startConversation({ mode: 'exit', forcedTrigger: req.trigger || 'bookLeave' });
    }, [bot.leaveRequest, enabled, isBooking]); // eslint-disable-line react-hooks/exhaustive-deps

    // Leaving an allowed page closes everything
    useEffect(() => {
        setOpen(false);
        setBubble(null);
        setLocal({ exitIntent: false, homeIdle: false, homeScroll: false });
        convRef.current += 1;
    }, [page]);

    // ── Local triggers: exit intent (desktop) + home idle/scroll ──────────
    useEffect(() => {
        if (!enabled || !page) return undefined;
        const start = Date.now();
        const fine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
        const onOut = (e) => {
            if (fine && !e.relatedTarget && e.clientY <= 0 && Date.now() - start > 10000) {
                setLocal(l => (l.exitIntent ? l : { ...l, exitIntent: true }));
            }
        };
        document.addEventListener('mouseout', onOut);

        let idleTimer = null;
        let onScroll = null;
        if (page === 'home') {
            idleTimer = setTimeout(() => setLocal(l => ({ ...l, homeIdle: true })), LIMITS.homeIdleMs);
            onScroll = () => {
                if (window.scrollY > LIMITS.homeScrollPx && Date.now() - start > 8000) {
                    setLocal(l => (l.homeScroll ? l : { ...l, homeScroll: true }));
                }
            };
            window.addEventListener('scroll', onScroll, { passive: true });
        }
        return () => {
            document.removeEventListener('mouseout', onOut);
            if (idleTimer) clearTimeout(idleTimer);
            if (onScroll) window.removeEventListener('scroll', onScroll);
        };
    }, [enabled, page]);

    // ── Entrance: let the page paint first, then pop in and wave once ─────
    useEffect(() => {
        if (!enabled || !page) { setReady(false); return undefined; }
        const t1 = setTimeout(() => { setReady(true); setMood('wave'); }, 1200);
        const t2 = setTimeout(() => { setGreeted(true); setMood(m => (m === 'wave' ? 'idle' : m)); }, 4600);
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, [enabled, page]);

    // ── Extended FAB behaviour (Material style) ───────────────────────────
    // Shrinks to the avatar while scrolling down, grows back on scroll up or near the top.
    // On the search page it starts extended for a few seconds, then stays compact
    // unless the user scrolls up, so it never sits on top of a Book button for long.
    useEffect(() => {
        if (!enabled || !page || open) return undefined;
        setExtended(true);
        // Booking pages are full of CTAs: show the label briefly, then stay avatar-only
        if (page === 'booking') {
            const t = setTimeout(() => setExtended(false), 4000);
            return () => clearTimeout(t);
        }
        let lastY = window.scrollY;
        let idle = null;
        const settle = page === 'search'
            ? setTimeout(() => { if (window.scrollY > 80) setExtended(false); }, 6000)
            : null;

        const onScroll = () => {
            const y = window.scrollY;
            const dy = y - lastY;
            lastY = y;
            if (y < 80) { setExtended(true); return; }
            if (dy > 6) setExtended(false);
            else if (dy < -6) setExtended(true);
            if (page === 'home') {
                clearTimeout(idle);
                idle = setTimeout(() => setExtended(true), 1500);
            }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            clearTimeout(idle);
            if (settle) clearTimeout(settle);
        };
    }, [enabled, page, open]);

    // Lets page CSS move other floating buttons (scroll-to-top) above the bot
    useEffect(() => {
        const on = enabled && !!page && ready;
        document.body.classList.toggle('has-leadbot', on);
        return () => document.body.classList.remove('has-leadbot');
    }, [enabled, page, ready]);

    // ── Pick the strongest active trigger ─────────────────────────────────
    const candidate = useMemo(() => {
        if (!page) return null;
        const active = {
            noRoutes: bot.noRoutes,
            allWaitlisted: bot.allWaitlisted,
            tatkalWindow: bot.tatkalWindow && bot.resultsCount > 0,
            returnedAfterAway: bot.returnedAfterAway,
            dateFlips: bot.dateFlips >= 3,
            deepScrollNoBook: bot.deepScrollNoBook,
            exitIntent: local.exitIntent,
            homeScroll: local.homeScroll,
            homeIdle: local.homeIdle,
            // booking pages
            bookingFailed: bot.bookingFailed,
            paymentFailed: bot.paymentFailed,
            bookError: bot.bookError,
            paymentDismissed: bot.paymentDismissed,
            formIdle: bot.formIdle,
            bookReturned: bot.bookReturned,
            bookExitIntent: local.exitIntent,
        };
        const list = page === 'search' ? SEARCH_TRIGGERS : page === 'booking' ? BOOKING_TRIGGERS : HOME_TRIGGERS;
        return list.find(t => active[t]) || null;
    }, [page, bot, local]);

    const isPainCandidate = isBooking && BOOKING_PAIN_TRIGGERS.includes(candidate);
    const nudgeKey = page === 'search'
        ? (bot.searchKey || 'search')
        : isBooking ? `${bot.bookingKey || 'booking'}:${isPainCandidate ? candidate : 'soft'}` : 'home';
    // Search/home: one lead per session. Booking: one lead per booking (a search callback doesn't block booking help)
    const alreadyHelped = isBooking ? bot.bookingLeadKeys.includes(bot.bookingKey) : bot.leadSubmitted;
    const reasonGiven = isBooking && bot.reasonKeys.includes(bot.bookingKey);

    useEffect(() => {
        if (!enabled || !page || !candidate || open || bubble) return undefined;
        if (suppressed || formBusy || alreadyHelped || bot.resultsLoading) return undefined;
        if (!isPainCandidate && bot.nudgesShown >= LIMITS.maxNudgesPerSession) return undefined;
        if (!isPainCandidate && reasonGiven) return undefined;
        if (bot.nudgedKeys.includes(nudgeKey) || isLeadBotSnoozed()) return undefined;

        const t = setTimeout(() => {
            const text = isBooking ? BOOKING_NUDGES[candidate](bot.booking) : NUDGES[candidate](bot.search);
            setBubble({ trigger: candidate, text });
            setMood(MOOD_FOR_TRIGGER(candidate));
            recordNudge(nudgeKey);
        }, LIMITS.nudgeDelayMs);
        return () => clearTimeout(t);
    }, [enabled, page, candidate, open, bubble, suppressed, formBusy, alreadyHelped, reasonGiven, bot.resultsLoading, bot.nudgesShown, nudgeKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Bubble auto-hides into an unread dot; hides instantly if something else takes over the screen
    useEffect(() => {
        if (!bubble) return undefined;
        // Hide instantly if the screen is taken over, or the user starts filling the form
        if (suppressed || formBusy) { setBubble(null); setUnread(true); setMood('idle'); return undefined; }
        const t = setTimeout(() => { setBubble(null); setUnread(true); setMood('idle'); }, LIMITS.bubbleAutoHideMs);
        return () => clearTimeout(t);
    }, [bubble, suppressed, formBusy]);

    // Keep the chat scrolled to the latest line
    useEffect(() => {
        if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
    }, [messages, typing, step]);

    // Esc closes
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') closePanel(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Conversation engine ───────────────────────────────────────────────
    const pushUser = (text) => setMessages(m => [...m, { id: msgId(), from: 'user', text }]);

    const say = useCallback(async (lines, nextStep, moodAfter = 'idle') => {
        const conv = convRef.current;
        setStep(null);
        for (const line of lines) {
            setTyping(true);
            setMood('think');
            await wait(450 + Math.min(line.length * 9, 750));
            if (convRef.current !== conv) return;
            setMessages(m => [...m, { id: msgId(), from: 'bot', text: line }]);
        }
        setTyping(false);
        setStep(nextStep);
        setMood(moodAfter);
    }, []);

    const callLine = () => {
        const status = getCallStatus(callUs);
        const next = describeNextOpen(status.nextOpen, callUs);
        return status.open || !next
            ? 'Our travel team will call you back shortly.'
            : `Our lines open ${next}, we'll call you then.`;
    };

    // Closing while a booking page is waiting to leave means "yes, I'm leaving",
    // unless stay is passed (the "Continue booking" chip).
    const closePanel = ({ stay = false } = {}) => {
        convRef.current += 1;
        setOpen(false);
        setTyping(false);
        setMood('idle');
        if (getLeadBotState().leaveRequest) resolveLeaveRequest(stay ? 'stay' : 'leave');
    };

    const goConfirmTrip = () => {
        const s = leadRef.current.search;
        if (!s) { say([COPY.askRoute], 'route'); return; }
        say([COPY.confirmTrip(tripSummary(s)), COPY.askFlex], 'flex');
    };

    const goAlert = () => {
        leadRef.current.intent = 'seat_alert';
        say([COPY.askPhoneAlert], 'phone');
    };

    const goHandoff = (label) => {
        resolveLeaveRequest('drop');   // we navigate to the planner instead of going back
        pushUser(label);
        say([COPY.handoffChat], 'end', 'wave').then(() => {
            setTimeout(() => { closePanel(); navigate('/chat'); }, 700);
        });
    };

    // ── Booking flows ─────────────────────────────────────────────────────
    // Reason only (no phone yet): saved as data on the lead row, no alerts. Fire and forget.
    const recordReason = (value) => {
        leadRef.current.reason = value;
        markReasonGiven(getLeadBotState().bookingKey);
        try {
            const p = submitContactAction('drop_reason', buildBookingFormData('', 'drop_reason'));
            if (p && typeof p.catch === 'function') p.catch(() => {});
        } catch { /* never break the chat */ }
    };

    const askBookingPhone = () => {
        leadRef.current.intent = 'booking_help';
        const pre = prefillDigits(leadRef.current.booking?.prefillPhone);
        if (pre) setPhone(p => p || pre);
        say([COPY.askPhoneCallback(callLine())], 'phone');
    };

    const followUpReason = (value) => {
        const r = reasonByValue(value) || {};
        if (r.offer === 'plan') return say([r.reply], 'planOffer', 'wave');
        if (r.offer === 'none') return say([r.reply], 'end', 'wave');
        return say([r.reply, COPY.offerCall].filter(Boolean), 'helpOffer');
    };

    const onBookReason = (o) => {
        if (o.value === 'stay') {
            pushUser(o.label);
            resolveLeaveRequest('stay');
            say([COPY.stay], 'end', 'wave').then(() => setTimeout(() => closePanel({ stay: true }), 900));
            return;
        }
        pushUser(o.label);
        if (o.value === 'other') {
            leadRef.current.reason = 'other';
            say([COPY.askOther], 'otherText');
            return;
        }
        recordReason(o.value);
        followUpReason(o.value);
    };

    const onOtherText = (text) => {
        leadRef.current.reasonText = text.slice(0, 200);
        pushUser(text);
        recordReason('other');
        followUpReason('other');
    };

    const onHelpOffer = (o) => {
        pushUser(o.label);
        if (o.value === 'yes') return askBookingPhone();
        return say([COPY.byeBooking], 'end', 'wave');
    };

    const onPlanOffer = (o) => {
        if (o.value === 'plan') return goHandoff(o.label);
        pushUser(o.label);
        return say([COPY.byeBooking], 'end', 'wave');
    };

    const startConversation = ({ fromNudge = false, accept = false, mode = null, forcedTrigger = null } = {}) => {
        convRef.current += 1;
        const trigger = forcedTrigger || (fromNudge ? bubble?.trigger : null);
        const nudgeText = fromNudge ? bubble?.text : null;

        leadRef.current = {
            trigger: trigger || 'manual',
            search: page === 'search' ? bot.search : null,
            booking: isBooking ? bot.booking : null,
            intent: 'callback',          // callback | seat_alert | booking_help
            flex: 'exact',
            modes: null,
            src: null,
            dest: null,
            date: '',
            reason: '',
            reasonText: '',
        };
        setBubble(null);
        setUnread(false);
        setOpen(true);
        setPhone('');
        setPhoneError('');
        setBusy(false);
        setStep(null);

        if (isBooking) {
            if (alreadyHelped) {
                setMessages([]);
                say([COPY.alreadyBooking], 'already', 'idle');
                return;
            }
            try {
                openContactModal?.(BOT_TRIGGER_TYPE[trigger || 'manual'] || BOT_TRIGGER_TYPE.manual, bookingSearchData(bot.booking));
            } catch { /* lead logging must never break the chat */ }

            if (mode === 'exit') {
                setMessages([]);
                say([COPY.exitAsk], 'exitReason', 'think');
                return;
            }
            if (fromNudge) {
                setMessages([{ id: msgId(), from: 'bot', text: nudgeText }]);
                pushUser(BOOKING_PRIMARY(trigger));
                const preset = BOOKING_TRIGGER_REASON[trigger];
                if (preset) { recordReason(preset); askBookingPhone(); }
                else if (trigger === 'bookReturned') say([COPY.bookingMenuAsk], 'bookMenu');
                else say([COPY.exitAsk], 'exitReason', 'think');
                return;
            }
            setMessages([]);
            say([COPY.bookingGreeting(bot.booking), COPY.bookingMenuAsk], 'bookMenu', 'wave');
            return;
        }

        if (bot.leadSubmitted) {
            setMessages([]);
            say([COPY.alreadyHave], 'already', 'idle');
            return;
        }

        // Log the open as a CLICKED lead row (trigger_type = bot_*), exactly like a banner click
        try {
            openContactModal?.(BOT_TRIGGER_TYPE[trigger || 'manual'] || BOT_TRIGGER_TYPE.manual, bot.search || {});
        } catch { /* lead logging must never break the chat */ }

        if (fromNudge) {
            setMessages([{ id: msgId(), from: 'bot', text: nudgeText }]);
            if (accept) {
                pushUser(NUDGE_PRIMARY(trigger));
                if (page === 'home') say(['Pick one and I will take it from there.'], 'homeMenu', 'wave');
                else goConfirmTrip();
            } else {
                pushUser('No thanks');
                say([COPY.alertOffer], 'alertOffer');
            }
            return;
        }

        setMessages([]);
        if (page === 'home') say([COPY.homeGreeting], 'homeMenu', 'wave');
        else say([COPY.searchGreeting(bot.search)], 'searchMenu', 'wave');
    };

    // Bubble actions
    const onBubbleYes = () => startConversation({ fromNudge: true, accept: true });
    const onBubbleNo = () => {
        if (!isBooking && bubble && PAIN_TRIGGERS.includes(bubble.trigger)) {
            startConversation({ fromNudge: true, accept: false });
        } else {
            setBubble(null);
            setMood('idle');
        }
    };
    const onBubbleClose = () => {
        snoozeLeadBot(LIMITS.snoozeHours);
        setBubble(null);
        setMood('idle');
    };

    // Step handlers
    const onHomeMenu = (o) => {
        if (o.value === 'plan') return goHandoff(o.label);
        pushUser(o.label);
        if (o.value === 'seats') return say([COPY.askRoute], 'route');
        leadRef.current.intent = 'callback';
        return say([COPY.askPhoneCallback(callLine())], 'phone');
    };

    const onSearchMenu = (o) => {
        if (o.value === 'plan') return goHandoff(o.label);
        pushUser(o.label);
        if (o.value === 'alert') return goAlert();
        return goConfirmTrip();
    };

    const onRoute = ({ src, dest, date }) => {
        Object.assign(leadRef.current, { src, dest, date });
        pushUser(`${shortPlace(src.label)} → ${shortPlace(dest.label)}, ${prettyShort(date)}`);
        say([COPY.askFlex], 'flex');
    };

    const onFlex = (o) => {
        leadRef.current.flex = o.value;
        pushUser(o.label);
        say([COPY.askModes], 'modes');
    };

    const onModes = (modes) => {
        leadRef.current.modes = modes;
        leadRef.current.intent = 'callback';
        pushUser(modes.join(' + '));
        say([COPY.askPhoneCallback(callLine())], 'phone');
    };

    const onAlertOffer = (o) => {
        pushUser(o.label);
        if (o.value === 'yes') return goAlert();
        return say([COPY.bye], 'end', 'wave');
    };

    const buildFormData = (digits) => {
        const l = leadRef.current;
        const s = l.search || {};
        const srcName = l.src ? l.src.label : (s.src_name || '');
        const srcCode = l.src ? l.src.code : (s.src_code || '');
        const destName = l.dest ? l.dest.label : (s.dest_name || '');
        const destCode = l.dest ? l.dest.code : (s.dest_code || '');
        const hasRoute = !!(srcName && destName);

        const today = todayISO();
        const base = l.date || s.travel_date_from || today;
        const days = (FLEX_OPTIONS.find(o => o.value === l.flex) || {}).days || 0;
        const fromRaw = addDays(base, -days);
        const from = fromRaw < today ? today : fromRaw;
        const to = addDays(base, days);

        const searchModes = normalizeModes(s.modes);
        const modes = l.modes || (hasRoute ? (searchModes.length ? searchModes : [...MODE_OPTIONS]) : []);

        // Keep only the classes that match the modes the user picked
        const classes = (s.seat_classes || []).filter(c =>
            (modes.includes('Train') && TRAIN_CLASSES.includes(c)) ||
            (modes.includes('Flight') && FLIGHT_CLASSES.includes(c)));

        return {
            src_name: srcName,
            src_code: srcCode,
            dest_name: destName,
            dest_code: destCode,
            travel_date_from: hasRoute ? from : '',
            travel_date_to: hasRoute ? to : '',
            modes,
            seat_classes: classes,
            phone: digits,
            country_code: countryCode,
            message: buildMessage({
                intent: l.intent, src: srcName, dest: destName, from, to, baseDate: base, modes, classes, flex: l.flex,
            }),
            // Extra bot fields. contact_lead_handler stores them when the hook passes them through;
            // lead_source is also inferred server side from trigger_type = bot_*.
            lead_source: 'lead_bot',
            trigger_type: BOT_TRIGGER_TYPE[l.trigger] || BOT_TRIGGER_TYPE.manual,
            bot_intent: l.intent,
            bot_page: page,
            date_flex: l.flex,
        };
    };

    // Booking leads: digits '' + intent 'drop_reason' for the reason-only save
    function buildBookingFormData(digits, intent) {
        const l = leadRef.current;
        const b = l.booking || {};
        const dropOnly = intent === 'drop_reason';
        return {
            ...bookingSearchData(b),
            ...(digits ? { phone: digits, country_code: countryCode } : {}),
            message: buildBookingMessage({ b, reason: l.reason, reasonText: l.reasonText, dropOnly }),
            lead_source: 'lead_bot',
            trigger_type: BOT_TRIGGER_TYPE[l.trigger] || BOT_TRIGGER_TYPE.manual,
            ...(dropOnly ? {} : { bot_intent: 'booking_help' }),
            bot_page: 'booking',
            date_flex: 'exact',
            booking_mode: b.mode || '',
            booking_ref: b.rcBookingId || '',
            booking_stage: b.stage || '',
            booking_amount: b.amount ? String(Math.round(Number(b.amount))) : '',
            drop_reason: l.reason || '',
            drop_reason_text: l.reasonText || '',
        };
    }

    const submitPhone = async () => {
        const digits = phone.replace(/\D/g, '');
        const err = validatePhone(countryCode, digits);
        if (err) { setPhoneError(err); return; }
        setPhoneError('');
        setBusy(true);

        const intent = leadRef.current.intent;
        const formData = isBooking ? buildBookingFormData(digits, 'booking_help') : buildFormData(digits);
        let ok = false;
        // Backend statuses stay the same two: the bot always submits as a callback request
        try { ok = await submitContactAction('callback_request', formData); } catch { ok = false; }
        setBusy(false);
        if (!ok) { setPhoneError(COPY.submitError); return; }

        if (isBooking) markBookingLeadSubmitted(getLeadBotState().bookingKey);
        else markLeadSubmitted();
        const phoneText = `${countryCode} ${digits}`;
        pushUser(phoneText);
        const status = getCallStatus(callUs);
        const next = describeNextOpen(status.nextOpen, callUs);
        const when = status.open || !next ? 'shortly' : `when our lines open ${next}`;
        const doneLine = isBooking
            ? COPY.doneBooking(phoneText, when, leadRef.current.booking?.rcBookingId)
            : intent === 'seat_alert' ? COPY.doneAlert(phoneText) : COPY.doneCallback(phoneText, when);
        say([doneLine], 'done', 'celebrate');
    };

    // ── Render ────────────────────────────────────────────────────────────
    if (!enabled || !page || typeof document === 'undefined') return null;

    // On mobile the dock gets out of the way completely while a form is being filled
    const dockHidden = (suppressed || (formBusy && isMobileView())) && !open;

    const renderControls = () => {
        if (typing || !step) return null;
        switch (step) {
            case 'homeMenu':
                return <Chips onPick={onHomeMenu} options={[
                    { value: 'seats', label: 'Need confirmed tickets', primary: true },
                    { value: 'plan', label: 'Plan a trip' },
                    { value: 'talk', label: 'Talk to a person' },
                ]} />;
            case 'searchMenu':
                return <Chips onPick={onSearchMenu} options={[
                    { value: 'seats', label: 'Get confirmed seats', primary: true },
                    { value: 'alert', label: 'Alert me if seats open' },
                    { value: 'plan', label: 'Plan another trip' },
                ]} />;
            case 'route':
                return <RouteStep onSubmit={onRoute} />;
            case 'flex':
                return <Chips onPick={onFlex} options={FLEX_OPTIONS} />;
            case 'modes':
                return <ModePicker initial={normalizeModes(leadRef.current.search?.modes)} onSubmit={onModes} />;
            case 'alertOffer':
                return <Chips onPick={onAlertOffer} options={[
                    { value: 'yes', label: 'Yes, alert me', primary: true },
                    { value: 'no', label: 'Not now' },
                ]} />;
            case 'phone':
                return (
                    <PhoneStep
                        intent={leadRef.current.intent}
                        countryCode={countryCode}
                        setCountryCode={(v) => { setCountryCode(v); setPhoneError(''); }}
                        phone={phone}
                        setPhone={(v) => { setPhone(v); setPhoneError(''); }}
                        error={phoneError}
                        busy={busy}
                        onSubmit={submitPhone}
                    />
                );
            case 'bookMenu':
                return <Chips onPick={onBookReason} options={
                    BOOKING_MENU_REASONS.map(v => ({ value: v, label: reasonLabel(v) }))
                } />;
            case 'exitReason':
                return <Chips onPick={onBookReason} options={[
                    { value: 'stay', label: 'Continue booking', primary: true },
                    ...BOOKING_REASONS.map(r => ({ value: r.value, label: r.label })),
                ]} />;
            case 'otherText':
                return <OtherTextStep onSubmit={onOtherText} />;
            case 'helpOffer':
                return <Chips onPick={onHelpOffer} options={[
                    { value: 'yes', label: 'Yes, call me', primary: true },
                    { value: 'no', label: 'No thanks' },
                ]} />;
            case 'planOffer':
                return <Chips onPick={onPlanOffer} options={[
                    { value: 'plan', label: 'Open trip planner', primary: true },
                    { value: 'no', label: 'No thanks' },
                ]} />;
            default:
                break;
        }

        // A booking page is waiting to leave: last step decides stay or go
        if (isBooking && bot.leaveRequest) {
            return <Chips onPick={(o) => closePanel({ stay: o.value === 'stay' })} options={[
                { value: 'stay', label: 'Continue booking', primary: true },
                { value: 'leave', label: 'Leave page' },
            ]} />;
        }

        switch (step) {
            case 'done':
            case 'already':
                return <Chips onPick={(o) => (o.value === 'plan' ? goHandoff(o.label) : closePanel())} options={[
                    { value: 'close', label: 'Thanks!', primary: true },
                    ...(page === 'home' ? [{ value: 'plan', label: 'Plan a trip meanwhile' }] : []),
                ]} />;
            case 'end':
            default:
                return <Chips onPick={closePanel} options={[{ value: 'close', label: 'Close' }]} />;
        }
    };

    return createPortal(
        <>
            {/* Dock: extended FAB + nudge bubble, bottom-right (where users expect chat) */}
            {!open && ready && (
                <div
                    data-leadbot-root=""
                    style={{ '--lb-offset': `${Number(bot.dockOffset) || 0}px` }}
                    className={`lb-dock fixed z-[900] right-4 sm:right-6
                                flex flex-col items-end ${dockHidden ? 'lb-hidden' : ''}`}
                    aria-hidden={dockHidden}
                >
                    {bubble && (
                        <div
                            role="status"
                            className="lb-bubble mb-3 w-[min(290px,calc(100vw-32px))] rounded-2xl rounded-br-md
                                       bg-white border border-slate-100 shadow-[0_16px_40px_-12px_rgba(15,23,42,0.35)] p-4"
                        >
                            <div className="flex items-center gap-2 mb-1.5">
                                <span className="text-[13px] font-semibold text-slate-900">{BOT_NAME}</span>
                                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" /> Online
                                </span>
                            </div>
                            <button
                                type="button"
                                onClick={onBubbleClose}
                                aria-label="Dismiss for today"
                                className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100"
                            >
                                <X size={14} />
                            </button>
                            <p className="pr-4 text-sm leading-snug text-slate-700">{bubble.text}</p>
                            <div className="mt-3 flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={onBubbleYes}
                                    className="min-h-[40px] px-4 rounded-full bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white text-sm font-semibold shadow-sm shadow-orange-500/30"
                                >
                                    {isBooking ? BOOKING_PRIMARY(bubble.trigger) : NUDGE_PRIMARY(bubble.trigger)}
                                </button>
                                <button
                                    type="button"
                                    onClick={onBubbleNo}
                                    className="min-h-[40px] px-3 rounded-full text-sm font-medium text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                                >
                                    No thanks
                                </button>
                            </div>
                        </div>
                    )}

                    {(() => {
                        // While a bubble is talking, the FAB stays compact so there's one message at a time
                        const showLabel = extended && !bubble && !formBusy;
                        const label = FAB_COPY({
                            page,
                            leadSubmitted: alreadyHelped,
                            pain: isBooking
                                ? (bot.paymentFailed || bot.bookError || bot.bookingFailed || bot.paymentDismissed)
                                : (bot.noRoutes || bot.allWaitlisted),
                        });
                        return (
                            <button
                                type="button"
                                onClick={() => startConversation()}
                                tabIndex={dockHidden ? -1 : 0}
                                aria-label={`${label.title} ${label.sub}`}
                                className={`lb-fab group relative flex items-center rounded-full bg-white border border-slate-200/80
                                            shadow-[0_10px_30px_-8px_rgba(15,23,42,0.35)] hover:shadow-[0_14px_36px_-8px_rgba(15,23,42,0.4)]
                                            focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2
                                            ${showLabel ? 'pl-4 pr-1.5' : 'p-1.5'}`}
                            >
                                <span className={`lb-fab-label overflow-hidden text-left ${showLabel ? 'lb-fab-label--on' : ''}`}>
                                    <span className="block whitespace-nowrap text-[13px] sm:text-sm font-semibold text-slate-900 leading-tight">{label.title}</span>
                                    <span className="block whitespace-nowrap text-[12px] sm:text-[13px] text-slate-500 leading-tight mt-0.5">{label.sub}</span>
                                </span>

                                <span className="relative shrink-0 w-[60px] h-[60px] sm:w-[68px] sm:h-[68px] rounded-full
                                                 bg-gradient-to-br from-sky-50 to-teal-100 flex items-center justify-center">
                                    {!greeted && <span className="lb-ring" aria-hidden="true" />}
                                    <Mascot mood={bubble ? mood : (greeted ? 'idle' : mood)} size={58} theme={MASCOT_THEME} className="sm:hidden" />
                                    <Mascot mood={bubble ? mood : (greeted ? 'idle' : mood)} size={66} theme={MASCOT_THEME} className="hidden sm:block" />
                                    {unread && (
                                        <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 rounded-full bg-red-500 border-2 border-white
                                                         text-[11px] font-bold text-white flex items-center justify-center" aria-hidden="true">1</span>
                                    )}
                                </span>
                            </button>
                        );
                    })()}
                </div>
            )}

            {open && (
                <>
                    {/* Mobile backdrop: tap to close */}
                    <div className="sm:hidden fixed inset-0 z-[940] bg-slate-900/25 lb-fade" onClick={closePanel} aria-hidden="true" />

                    <div
                        data-leadbot-root=""
                        role="dialog"
                        aria-label={`${BOT_NAME}, RouteChef travel help`}
                        style={kbInset ? { bottom: kbInset } : undefined}
                        className="lb-sheet fixed z-[950] inset-x-0 bottom-0 sm:inset-x-auto sm:right-6 sm:bottom-6 sm:w-[380px]
                                   max-h-[75dvh] sm:max-h-[min(580px,80vh)] flex flex-col bg-white
                                   rounded-t-3xl sm:rounded-2xl shadow-2xl border border-slate-100"
                    >
                        {/* Header */}
                        <div className="shrink-0 flex items-center gap-3 px-4 pt-3 pb-3 border-b border-slate-100">
                            <div className="shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-sky-50 to-teal-100 flex items-center justify-center">
                                <Mascot mood={mood} size={46} theme={MASCOT_THEME} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-slate-900 leading-tight">{BOT_NAME}</p>
                                <p className="flex items-center gap-1 text-xs text-slate-500 leading-tight mt-0.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" /> RouteChef travel help · Online
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closePanel}
                                aria-label="Close chat"
                                className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Messages */}
                        <div ref={listRef} className="flex-1 min-h-[140px] overflow-y-auto overscroll-contain px-4 py-4 space-y-2.5" aria-live="polite">
                            {messages.map(m => (
                                <div key={m.id} className={`flex ${m.from === 'user' ? 'justify-end' : 'justify-start'} lb-msg-in`}>
                                    <div
                                        className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-snug rounded-2xl
                                            ${m.from === 'user'
                                                ? 'bg-orange-500 text-white rounded-br-md'
                                                : 'bg-slate-100 text-slate-800 rounded-bl-md'}`}
                                    >
                                        {m.text}
                                    </div>
                                </div>
                            ))}
                            {typing && <TypingDots />}
                        </div>

                        {/* Controls for the current step */}
                        <div className="shrink-0 border-t border-slate-100 px-4 pt-3 pb-[max(0.875rem,env(safe-area-inset-bottom))] min-h-[64px]">
                            {renderControls()}
                        </div>
                    </div>
                </>
            )}
        </>,
        document.body
    );
};

export default LeadBot;
